import {
  RETRYABLE, type CommandResult, type CommandStatus, type CommandWire, type PullResponse,
} from '@gk/contracts'
import { getMeta, localOnlyTables, setMeta, MIRROR_STORES, type GkDb, type OutboxRow } from './db.ts'
import { TransportError, type Transport } from './transport.ts'
import { settleTransient, sweepAtStart } from './transient.ts'

/** The sync engine (ARCHITECTURE 9.4, 9.6): push the outbox, pull the delta, never lose a command, never apply one twice.
 *  Every safety property rests on the server's idempotency (command ids), so the engine may re-send freely:
 *  a lost response, a crash mid-send, two tabs, a retry after a timeout: all end in the same final state. */

export interface EngineState {
  phase: 'idle' | 'syncing'
  online: boolean | null          // null until the first answer or failure
  lastSyncAt: number | null
  lastError: string | null
  authRequired: boolean
  frozen: boolean
}
export interface SyncSummary { sent: number; applied: number; refused: number; pulled: number; skipped: boolean }

export interface EngineOptions {
  db: GkDb
  transport: Transport
  orgId: string
  appVersion?: string
  platform?: () => { persisted: boolean | null; standalone: boolean | null }
  clock?: () => number
  random?: () => number
  maxBatch?: number
  lock?: <T>(name: string, fn: () => Promise<T>) => Promise<T | undefined>
  /** Runs after every cycle that reached the server (the photo upload lane). Its failure never fails the sync. */
  afterSync?: () => Promise<unknown>
}

export const backoffMs = (attempts: number, random: () => number): number =>
  Math.round(Math.min(300_000, 2_000 * 2 ** Math.max(0, attempts - 1)) * (0.75 + random() * 0.5))

/** One sync at a time across tabs (Web Locks); a plain mutex where the API does not exist (tests). */
function defaultLock(name: string) {
  const fallback = new Set<string>()
  return async <T,>(_n: string, fn: () => Promise<T>): Promise<T | undefined> => {
    const locks = (globalThis as { navigator?: Navigator }).navigator?.locks
    if (locks) return locks.request(name, { ifAvailable: true }, async (l) => (l ? fn() : undefined)) as Promise<T | undefined>
    if (fallback.has(name)) return undefined
    fallback.add(name)
    try { return await fn() } finally { fallback.delete(name) }
  }
}

export class SyncEngine {
  state: EngineState = { phase: 'idle', online: null, lastSyncAt: null, lastError: null, authRequired: false, frozen: false }
  private listeners = new Set<() => void>()
  private inflight: Promise<SyncSummary> | null = null
  private backoffUntil = 0
  /** the start-up sweep of transient rows (sync/transient.ts) ran: once per engine, i.e. once per sign-in or app start */
  private swept = false
  private readonly o: Required<Pick<EngineOptions, 'clock' | 'random' | 'maxBatch' | 'appVersion'>> & EngineOptions

  constructor(opts: EngineOptions) {
    this.o = {
      clock: Date.now, random: Math.random, maxBatch: 25, appVersion: 'dev',
      ...opts,
      lock: opts.lock ?? defaultLock(`gk-sync:${opts.db.name}`),
    }
    // the last successful sync survives a reload: the badge must not say "never" for a device that synced a minute ago
    void getMeta<number>(opts.db, 'lastPullAt').then((t) => { if (t && this.state.lastSyncAt === null) this.set({ lastSyncAt: t }) }).catch(() => {})
  }

  subscribe(fn: () => void): () => void { this.listeners.add(fn); return () => this.listeners.delete(fn) }
  private set(patch: Partial<EngineState>) { this.state = { ...this.state, ...patch }; this.listeners.forEach((f) => f()) }
  /** The radio's own word (the browser's `offline` / `online` events, sync/scheduler.ts, M23-0): false the moment the network is gone, so the badge
   *  says "Hors ligne" before any send fails; null when the browser says it is back (unknown until the next answer: a captive portal is "online" too). */
  setOnline(online: boolean | null): void { if (this.state.online !== online) this.set({ online }) }
  /** An answer that arrives after the radio went off (the request was in flight when the `offline` event fired) must not say "online" again: the
   *  badge would read "Tout est envoyé" with no network until the next failed cycle (M23 item 7, review). The radio's own word decides. */
  private answeredOnline(): boolean { return typeof navigator === 'undefined' || navigator.onLine !== false }

  /** Run one full cycle: push until nothing is due, pull to the high-water mark, report a heartbeat. */
  sync(opts: { force?: boolean } = {}): Promise<SyncSummary> {
    if (this.inflight) return this.inflight
    this.inflight = this.cycle(opts).then(async (sum) => {
      if (!sum.skipped && this.state.online !== false && this.o.afterSync) { try { await this.o.afterSync() } catch { /* the lane retries at the next cycle */ } }
      return sum
    }).finally(() => { this.inflight = null })
    return this.inflight
  }

  private async cycle(opts: { force?: boolean }): Promise<SyncSummary> {
    const sum: SyncSummary = { sent: 0, applied: 0, refused: 0, pulled: 0, skipped: false }
    if (!opts.force && this.o.clock() < this.backoffUntil) { sum.skipped = true; return sum }
    const out = await this.o.lock!('sync', async () => {
      this.set({ phase: 'syncing' })
      try {
        await this.recoverSending()
        // Once per engine, before anything is sent: what an older build left of positions goes (answered rows, rows waiting since too long).
        if (!this.swept) { try { await sweepAtStart(this.o.db, this.o.clock()); this.swept = true } catch { /* tried again at the next cycle */ } }
        // Before the very first push the device does not know the epoch: pull first, so no command is sent into EPOCH_CHANGED.
        let pulledFirst = false
        if (!(await getMeta<string>(this.o.db, 'epoch'))) { await this.pullLoop(sum); pulledFirst = true }
        const sentBefore = sum.sent
        const pushed = await this.pushLoop(sum)
        // A second pull is only worth it if something was sent (to resolve it) or no first pull happened.
        if (pushed !== 'stop' && !(pulledFirst && sum.sent === sentBefore)) await this.pullLoop(sum)
        if (this.state.online) await this.heartbeat()
        if (this.state.online) this.set({ lastSyncAt: this.o.clock(), lastError: null })
      } finally {
        this.set({ phase: 'idle' })
      }
      return sum
    })
    if (out === undefined) { sum.skipped = true; return sum }
    return out
  }

  // ---------------------------------------------------------------------------------------------------- push
  /** A crash mid-send leaves `sending` rows: the server may or may not have applied them; re-sending is safe. */
  private async recoverSending() {
    await this.o.db.outbox.where('status').equals('sending').modify({ status: 'queued' })
  }

  private async due(limit: number): Promise<OutboxRow[]> {
    const now = this.o.clock()
    const rows = await this.o.db.outbox.where('status').anyOf([...RETRYABLE]).sortBy('local_seq')
    return rows.filter((r) => (r.next_attempt_at ?? 0) <= now).slice(0, limit)
  }

  private toWire(r: OutboxRow): CommandWire {
    const { command_id, name, version, org_id, site_id, aggregate_id, depends_on, payload, occurred_at, known_seq, known_epoch, clock_offset_ms, actor_person_id, nonce, kiosk_unlock_id } = r
    return { command_id, name, version, org_id, site_id, depends_on, payload, occurred_at, known_seq, clock_offset_ms,
             ...(aggregate_id ? { aggregate_id } : {}), ...(known_epoch ? { known_epoch } : {}), ...(actor_person_id ? { actor_person_id } : {}), ...(nonce ? { nonce } : {}), ...(kiosk_unlock_id ? { kiosk_unlock_id } : {}) }
  }

  private async pushLoop(sum: SyncSummary): Promise<'done' | 'stop'> {
    const { db, transport } = this.o
    const deviceId = await this.deviceId()
    let batch = this.o.maxBatch
    let authRetried = false
    for (let guard = 0; guard < 500; guard++) {
      const rows = await this.due(batch)
      if (!rows.length) return 'done'
      await db.outbox.where('command_id').anyOf(rows.map((r) => r.command_id)).modify({ status: 'sending', last_attempt_at: new Date(this.o.clock()).toISOString() })
      let resp
      try {
        resp = await transport.submit(deviceId, (await getMeta<string>(db, 'epoch')) ?? ZERO_EPOCH, rows.map((r) => this.toWire(r)))
      } catch (e) {
        await db.outbox.where('command_id').anyOf(rows.map((r) => r.command_id)).modify({ status: 'queued' })
        const err = e instanceof TransportError ? e : new TransportError('server', String(e))
        if (err.kind === 'timeout') {
          // The whole call was cancelled (57014). Halve the batch; a command that still times out ALONE twice is reported
          // through mark_command_timeout, a separate short transaction, so the queue moves on.
          if (rows.length > 1) { batch = Math.max(1, Math.floor(rows.length / 2)); continue }
          const row = rows[0]!
          const timeouts = row.timeouts + 1
          await db.outbox.update(row.command_id, { timeouts })
          if (timeouts >= 2) {
            try { await this.applyResult(row, await transport.markTimeout(deviceId, this.toWire(row)), sum) } catch { this.backoff(1); return 'stop' }
          }
          continue
        }
        if (err.kind === 'auth') {
          if (!authRetried && (await transport.refreshAuth())) { authRetried = true; continue }
          this.set({ authRequired: true, lastError: err.message }); return 'stop'
        }
        if (err.kind === 'network') { this.set({ online: false, lastError: err.message }); this.backoff(1); return 'stop' }
        if (err.kind === 'frozen') { this.set({ frozen: true, online: false, lastError: err.message }); this.backoff(4); return 'stop' }
        this.set({ lastError: err.message }); this.backoff(2); return 'stop'
      }
      this.set({ online: this.answeredOnline(), frozen: false, authRequired: false })
      const byId = new Map(resp.results.map((r) => [r.command_id, r]))
      let progressed = false
      let epochChanged = false
      for (const row of rows) {
        const r = byId.get(row.command_id)
        if (!r) { await db.outbox.update(row.command_id, { status: 'queued' }); continue }   // not answered: still ours to send
        if (r.code === 'EPOCH_CHANGED') epochChanged = true
        else if (r.status !== 'deferred') progressed = true
        await this.applyResult(row, r, sum)
      }
      sum.sent += rows.length
      if (epochChanged) { await this.pullLoop(sum); continue }          // learn the new epoch, then re-send (idempotent)
      batch = Math.min(this.o.maxBatch, batch * 2)
      if (!progressed) return 'done'                                    // only deferred: back off, never a hot loop
    }
    return 'done'
  }

  private async applyResult(row: OutboxRow, r: CommandResult, sum: SyncSummary) {
    const { db } = this.o
    const now = this.o.clock()
    switch (r.status) {
      case 'applied':
      case 'applied_with_exception': {
        const epoch = (await getMeta<string>(db, 'epoch')) ?? ZERO_EPOCH
        await db.transaction('rw', [db.outbox, db.archive, ...localOnlyTables(db)], async () => {      // the local-only stores: settleTransient may clean one
          await db.outbox.update(row.command_id, { status: r.status as CommandStatus, exceptions: r.exceptions ?? [], server_code: r.code ?? null, server_result: r.result ?? null, next_attempt_at: undefined })
          await db.archive.put({ command_id: row.command_id, name: row.name, aggregate_id: row.aggregate_id, applied_at: new Date(now).toISOString(), epoch, local_seq: row.local_seq })
          await settleTransient(db, row.command_id)                    // a position leaves the device once answered (sync/transient.ts)
        })
        sum.applied++
        break
      }
      case 'rejected': case 'conflict': case 'error': {
        // The effect never happened: its projection must disappear from the screen; the command stays visible, attributed to the user.
        await db.transaction('rw', [db.outbox, db.overlay, ...localOnlyTables(db)], async () => {
          await db.outbox.update(row.command_id, { status: r.status as CommandStatus, server_code: r.code ?? null, server_result: r.result ?? null, next_attempt_at: undefined })
          await db.overlay.where('command_id').equals(row.command_id).delete()
          await settleTransient(db, row.command_id)                    // a refused position keeps no coordinate
        })
        sum.refused++
        break
      }
      case 'blocked': case 'transient': {
        const attempts = row.attempts + 1
        const epoch = r.code === 'EPOCH_CHANGED'
        await db.outbox.update(row.command_id, { status: epoch ? 'queued' : (r.status as CommandStatus), attempts: epoch ? row.attempts : attempts, server_code: r.code ?? null, next_attempt_at: epoch ? undefined : now + backoffMs(attempts, this.o.random) })
        break
      }
      case 'deferred':
        await db.outbox.update(row.command_id, { status: 'queued', next_attempt_at: now + 2_000 })
        break
    }
  }

  private backoff(attempt: number) { this.backoffUntil = this.o.clock() + backoffMs(attempt, this.o.random) }

  // ---------------------------------------------------------------------------------------------------- pull
  private async pullLoop(sum: SyncSummary) {
    const { db, transport, orgId } = this.o
    for (let guard = 0; guard < 500; guard++) {
      const cursor = (await getMeta<number>(db, 'cursor')) ?? 0
      const epoch = (await getMeta<string>(db, 'epoch')) ?? null
      let page: PullResponse
      try {
        page = await transport.pull(orgId, cursor, epoch)
      } catch (e) {
        const err = e instanceof TransportError ? e : new TransportError('server', String(e))
        if (err.kind === 'network') { this.set({ online: false, lastError: err.message }); this.backoff(1) }
        else if (err.kind === 'auth') { if (!(await transport.refreshAuth())) this.set({ authRequired: true, lastError: err.message }) }
        else if (err.kind === 'frozen') { this.set({ frozen: true, online: false }); this.backoff(4) }
        else this.set({ lastError: err.message })
        return
      }
      this.set({ online: this.answeredOnline(), frozen: false })
      if (page.epoch_changed || page.resnapshot_required) {
        // M10 adds the archive re-send; until then the safe answer is to resnapshot the mirror and keep the outbox.
        await this.resnapshot(page.epoch)
        continue
      }
      await applyPull(db, page, this.o.clock())
      sum.pulled += Object.values(page.tables ?? {}).reduce((n, rows) => n + rows.length, 0)
      if (!page.has_more) return
    }
  }

  private async resnapshot(epoch: string) {
    const { db } = this.o
    await db.transaction('rw', [db.meta, db.overlay, ...MIRROR_STORES.map((s) => db.table(s))], async () => {
      for (const s of MIRROR_STORES) await db.table(s).clear()
      await setMeta(db, 'cursor', 0); await setMeta(db, 'knownSeq', 0); await setMeta(db, 'epoch', epoch)
    })
  }

  // ---------------------------------------------------------------------------------------------------- misc
  async deviceId(): Promise<string> {
    const { db } = this.o
    let id = await getMeta<string>(db, 'deviceId')
    if (!id) { id = crypto.randomUUID(); await setMeta(db, 'deviceId', id) }
    return id
  }

  private async heartbeat() {
    const { db, transport } = this.o
    try {
      const pending = await db.outbox.where('status').anyOf([...RETRYABLE, 'sending']).toArray()
      const oldest = pending.map((r) => r.created_at).sort()[0] ?? null
      const p = this.o.platform?.() ?? { persisted: null, standalone: null }
      await transport.heartbeat(await this.deviceId(), this.o.orgId, {
        outbox_depth: pending.length, oldest_pending_at: oldest, storage_persisted: p.persisted, standalone: p.standalone,
        app_version: this.o.appVersion, last_cursor: (await getMeta<number>(db, 'cursor')) ?? 0,
        clock_offset_ms: (await getMeta<number>(db, 'clockOffsetMs')) ?? 0,
      })
    } catch { /* a heartbeat is best-effort and rate-limited server-side */ }
  }

  /** Soonest time a waiting command becomes due, for the scheduler. */
  async nextWakeAt(): Promise<number | null> {
    const rows = await this.o.db.outbox.where('status').anyOf([...RETRYABLE]).toArray()
    const times = rows.map((r) => r.next_attempt_at ?? 0).filter((t) => t > this.o.clock())
    if (this.backoffUntil > this.o.clock()) times.push(this.backoffUntil)
    return times.length ? Math.min(...times) : null
  }
}

/** Apply one pull page in ONE transaction (ARCHITECTURE 9.1): mirror rows, command results, the cursor and the knowledge
 *  marker move together. `knownSeq` (the knowledge marker stamped on new commands) only advances once the LAST page is applied. */
export async function applyPull(db: GkDb, page: PullResponse, nowMs: number): Promise<void> {
  const stores = MIRROR_STORES.map((s) => db.table(s))
  await db.transaction('rw', [db.meta, db.outbox, db.overlay, db.archive, ...stores, ...localOnlyTables(db)], async () => {
    for (const [table, rows] of Object.entries(page.tables ?? {})) {
      const store = db.mirrorStore(table)
      if (store && rows.length) await store.bulkPut(rows as Array<Record<string, unknown> & { id: string }>)
    }
    // M11: rows the server removed (the retention job, past their period) leave the mirror too, after this page's rows so that a row stamped LATER than its
    // tombstone (created again since) stays. The unsent photo bytes (`blobs`) are the outbox's, never the mirror's: a tombstone never touches them.
    for (const tomb of page.tombstones ?? []) {
      const store = db.mirrorStore(tomb.table)
      if (!store) continue
      const row = await store.get(tomb.id)
      if (row && !(typeof row.sync_seq === 'number' && row.sync_seq > tomb.sync_seq)) await store.delete(tomb.id)
    }
    // the server's own rows have arrived: drop the local INSERT projections they replace (patches are handled below)
    for (const o of await db.overlay.toArray()) {
      if (o.op === 'patch') continue
      const store = db.mirrorStore(o.table)
      if (store && (await store.get(o.id))) await db.overlay.delete(o.key)
    }
    // resolve outbox entries from the device's own command results (this is what makes a lost push response harmless)
    for (const cr of page.command_results ?? []) {
      const row = await db.outbox.get(cr.command_id)
      if (!row) continue
      if (['applied', 'applied_with_exception'].includes(cr.status) && !['applied', 'applied_with_exception'].includes(row.status)) {
        await db.outbox.update(cr.command_id, { status: cr.status, exceptions: cr.exceptions ?? [], server_code: cr.error_code, server_result: cr.result, next_attempt_at: undefined })
        await db.archive.put({ command_id: cr.command_id, name: cr.name, aggregate_id: cr.aggregate_id ?? undefined, applied_at: cr.applied_at ?? new Date(nowMs).toISOString(), epoch: page.epoch, local_seq: row.local_seq })
        await settleTransient(db, cr.command_id)
      } else if (['rejected', 'conflict', 'error'].includes(cr.status) && RETRYABLE.has(row.status)) {
        await db.outbox.update(cr.command_id, { status: cr.status, server_code: cr.error_code, server_result: cr.result, next_attempt_at: undefined })
        await db.overlay.where('command_id').equals(cr.command_id).delete()
        await settleTransient(db, cr.command_id)
      }
    }
    // A PATCH projection (an offline edit) is laid over the server's row until its command is resolved; once the last page of this
    // pull is applied the mirror carries the server's version, so a resolved command's patch can go.
    if (!page.has_more) {
      for (const o of await db.overlay.where('table').notEqual('').toArray()) {
        if (o.op !== 'patch') continue
        const row = await db.outbox.get(o.command_id)
        if (!row || !RETRYABLE.has(row.status) && row.status !== 'sending') await db.overlay.delete(o.key)
      }
    }
    if (page.next_cursor !== undefined) await setMeta(db, 'cursor', page.next_cursor)
    if (!page.has_more && page.hi !== undefined) await setMeta(db, 'knownSeq', page.hi)
    if (page.epoch) await setMeta(db, 'epoch', page.epoch)
    if (page.me) await setMeta(db, 'me', page.me)
    if (page.server_time) await setMeta(db, 'clockOffsetMs', nowMs - new Date(page.server_time).getTime())
    await setMeta(db, 'lastPullAt', nowMs)
  })
}

export const ZERO_EPOCH = '00000000-0000-0000-0000-000000000000'
